import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../config/env';
import { useFeedStream } from '../hooks/useFeedStream';
import { api } from '../lib/api';
import type { FeedItem, LaunchSummary } from '../lib/api';
import { difficultyBits, fmtBits } from '../lib/bits';
import { formatEthFloat, formatPct } from '../lib/format';
import { FeedLog } from '../components/FeedLog';
import { MiniStrip } from '../components/MiniStrip';
import { LaunchImage } from '../components/bits';
import { PixelPanel, Stat } from '../components/pixel/Pixel';
import { PixelIcon } from '../components/pixel/icons';
import type { IconName } from '../components/pixel/icons';
import { LiveForge } from './landing/LiveForge';
import { ProtocolNumbers } from './landing/ProtocolNumbers';
import s from './Landing.module.css';
import { P, lotsIn } from '../lib/params';

const LIFECYCLE: { icon: IconName; title: string; text: string }[] = [
  { icon: 'flame', title: 'Birth', text: `A creator names the token. ${P.totalSupply} tokens are minted to the launch contract. The creator receives none.` },
  { icon: 'hammer', title: 'Mine + buy', text: `Each primary buy needs a proof of work bound to the buyer's wallet. One proof buys one lot of ${P.lot} tokens at the current price.` },
  { icon: 'curve', title: 'Curve', text: `The price follows a fixed curve, rising ${P.priceMultiple}× from the first lot to the last. Anyone can sell back to the curve without a proof.` },
  { icon: 'pool', title: 'Graduation', text: `When all ${P.lots} lots are in circulation, the ${P.reserve} reserve and ${P.lpSupply} tokens go into a Uniswap V2 pool in the same transaction.` },
  { icon: 'arrow', title: 'Open market', text: "Trading moves to the pool and mining ends. The pool's LP tokens stay in the launch contract; it has no function to withdraw them." },
];

export function Landing() {
  const stats = useQuery({ queryKey: ['stats'], queryFn: api.stats, refetchInterval: 30_000 });
  return (
    <div className="page">
      <section className={s.hero}>
        <div className={s.heroText}>
          <span className={s.kicker}>
            <PixelIcon name="anvil" px={2} /> Proof-of-work token launches
          </span>
          <h1 className={s.headline}>
            Mine your <span className={s.hot}>entry.</span>
          </h1>
          <p className={s.sub}>You can&rsquo;t ape the curve. You forge your way onto it.</p>
          <p className={s.lede}>
            Every primary buy starts with a proof of work bound to your wallet. One proof buys one lot at the curve price. Selling back needs no work.
          </p>
          <div className={s.ctas}>
            <Link className="btn btn-primary btn-lg" to="/launches" data-testid="cta-explore">
              <PixelIcon name="hammer" px={2} /> Explore launches
            </Link>
            <Link className="btn btn-ghost btn-lg" to="/create" data-testid="cta-create">
              Launch a token
            </Link>
          </div>
        </div>
        <PixelPanel title="The network, live" icon="spark" status={stats.isError ? 'Offline' : 'Live'} tone={stats.isError ? 'bad' : 'ok'} className={s.statsPanel}>
          <div className={s.stats}>
            <Stat label="Launches" value={stats.data ? stats.data.launches.toLocaleString('en-US') : '—'} />
            <Stat label="Active" value={stats.data ? stats.data.active.toLocaleString('en-US') : '—'} />
            <Stat label="Proofs, 24 h" value={stats.data ? stats.data.forges24h.toLocaleString('en-US') : '—'} />
            <Stat label="Graduated" value={stats.data ? stats.data.graduated.toLocaleString('en-US') : '—'} />
          </div>
          <p className="small muted" style={{ margin: '14px 0 0' }}>
            {stats.isError ? `The indexer at ${API_URL} is not reachable.` : 'Counts from the indexer. Every lot on every curve was paid for with a proof of work.'}
          </p>
        </PixelPanel>
      </section>

      <PixelPanel title="The forge, live" icon="flame" status="Live" tone="ok" flush className={s.scenePanel}>
        <LiveForge />
      </PixelPanel>

      <LiveSection />

      <section className={s.section} aria-labelledby="lifecycle">
        <h2 id="lifecycle" className={s.h2}>
          Five steps, every launch
        </h2>
        <ol className={s.lifecycle}>
          {LIFECYCLE.map((l, i) => (
            <li key={l.title} className="px-panel">
              <div className={s.stepHead}>
                <span className={s.stepNum}>{i + 1}</span>
                <PixelIcon name={l.icon} px={3} />
              </div>
              <strong>{l.title}</strong>
              <span>{l.text}</span>
            </li>
          ))}
        </ol>
      </section>

      <ProtocolNumbers />
    </div>
  );
}

function LiveSection() {
  const recent = useQuery({ queryKey: ['recent'], queryFn: () => api.recent(30) });
  const launches = useQuery({ queryKey: ['launches', 'landing'], queryFn: () => api.launches({ view: 'new', limit: 24 }), refetchInterval: 20_000 });
  const [live, setLive] = useState<FeedItem[]>([]);
  useFeedStream({
    onItem: (i) => setLive((prev) => (prev.some((p) => p.id === i.id) ? prev : [i, ...prev].slice(0, 60))),
    onReorg: () => setLive([]),
  });

  const seen = new Set<string>();
  // challenge openings are shown on each token's log; here they would crowd out trades
  const feed = [...live, ...(recent.data?.items ?? [])].filter((i) => i.type !== 'challenge' && (seen.has(i.id) ? false : (seen.add(i.id), true)));
  const active = (launches.data?.items ?? []).filter((l) => l.phase === 'active').slice(0, 4);
  const down = recent.isError && launches.isError;

  return (
    <section className={s.section} aria-labelledby="live">
      <div className={s.sectionHead}>
        <h2 id="live" className={s.h2}>
          Live forges
        </h2>
        <Link to="/launches" className="pixel small">
          All launches →
        </Link>
      </div>
      {down ? (
        <div className="notice" data-tone="warn">
          The indexer is not reachable at <span className="mono">{API_URL}</span>. Live activity will appear here when it is.
        </div>
      ) : (
        <div className={s.liveGrid}>
          <div className={s.cards}>
            {active.length ? (
              active.map((l) => <LaunchCard key={l.address} l={l} />)
            ) : (
              <div className="px-panel">
                <div className="px-body muted">No active launches right now.</div>
              </div>
            )}
          </div>
          <PixelPanel title="Across all launches" icon="spark" status="Feed">
            <FeedLog items={feed} withSymbol max={14} empty="No activity yet." testId="global-feed" />
          </PixelPanel>
        </div>
      )}
    </section>
  );
}

function LaunchCard({ l }: { l: LaunchSummary }) {
  return (
    <Link to={`/t/${l.address}`} className={`px-panel ${s.card}`}>
      <div className={s.cardHead}>
        <LaunchImage src={l.metadata?.image} symbol={l.symbol} size={36} />
        <span className={s.cardName}>
          <strong>${l.symbol}</strong>
          <span className="muted small">{l.name}</span>
        </span>
        <span className={s.cardBits}>
          <strong>{l.difficultyFloat ? fmtBits(difficultyBits(l.difficultyFloat)) : '—'}</strong>
          <span className="label">bits</span>
        </span>
      </div>
      <MiniStrip launch={l.address} sold={BigInt(l.sold)} buys={l.buys} sells={l.sells} label={`${l.lotsForged} lots forged`} size="medium" />
      <div className={s.cardFoot}>
        <span>
          <span className="label">Lots</span> {lotsIn(BigInt(l.sold))}/{P.lots}
        </span>
        <span>
          <span className="label">Curve</span> {formatPct(l.progress, 1)}
        </span>
        <span>
          <span className="label">Price</span> {formatEthFloat(l.spotPriceFloat, 3)}
        </span>
      </div>
    </Link>
  );
}
